import { Outlet, Link, useLocation } from "react-router-dom";
import {
  Button,
  Avatar,
  AvatarFallback,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@asm/ui";
import {
  LayoutDashboard,
  Scan,
  GitCompare,
  Calendar,
  Globe,
  Box,
  Settings,
  LogOut,
  Menu,
  X,
  Sun,
  Moon,
  Monitor,
  Radar,
  ChevronRight,
} from "lucide-react";
import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { useAuthStore } from "@/lib/auth";
import { useTheme } from "@/lib/theme";
import { FEATURES, type FeatureFlag } from "@/lib/features";
import { schedulesApi } from "@/lib/api";

export function DashboardLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const { user, logout } = useAuthStore();
  const { t } = useTranslation("common");
  const { theme, setTheme, resolvedTheme } = useTheme();

  // Fetch tier limits to gate features by subscription
  const { data: tierLimits } = useQuery({
    queryKey: ["tierLimits"],
    queryFn: () => schedulesApi.getTierLimits(),
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  // Navigation items with optional feature flags and tier gates
  const allNavigation = [
    { name: t("nav.dashboard"), href: "/", icon: LayoutDashboard, exact: true },
    { name: t("nav.scans"), href: "/scans", icon: Scan },
    { name: t("nav.comparisons"), href: "/comparisons", icon: GitCompare, feature: "comparisons" as FeatureFlag },
    { name: t("nav.schedules"), href: "/schedules", icon: Calendar },
    { name: t("nav.domains"), href: "/domains", icon: Globe },
    { name: t("nav.assets", "Asset Intelligence"), href: "/assets", icon: Box, feature: "assetInventory" as FeatureFlag, tierGate: "asset_inventory_enabled" as const },
    { name: t("nav.settings"), href: "/settings", icon: Settings },
  ];

  // Filter navigation based on feature flags and tier limits
  const navigation = useMemo(() =>
    allNavigation.filter(item => {
      if (item.feature && !FEATURES[item.feature]) return false;
      if (item.tierGate && tierLimits && !tierLimits[item.tierGate]) return false;
      return true;
    }),
    [t, tierLimits]
  );

  const tools: typeof navigation = [
    // { name: "Port Scanner", href: "/tools/portscan", icon: Radio },
  ];

  const handleLogout = async () => {
    await logout();
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar - Tactical Command Center */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-72 transform transition-transform duration-300 ease-out lg:translate-x-0 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        {/* Sidebar background */}
        <div className="absolute inset-0 bg-slate-950 border-r border-border/50" />

        {/* Sidebar content */}
        <div className="relative h-full flex flex-col z-10">
          {/* Header / Logo */}
          <div className="flex h-20 items-center justify-between px-5 border-b border-border/30">
            <Link to="/" className="flex items-center gap-3 group">
              <div className="h-10 w-10 rounded-xl bg-cyan-500 flex items-center justify-center">
                <Radar className="h-5 w-5 text-white" />
              </div>
              <div>
                <span className="font-bold text-lg text-white tracking-tight">ASM Toolkit</span>
                <span className="text-cyan-300/50 text-[10px] block tracking-wider uppercase">by Sertua</span>
              </div>
            </Link>
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden text-cyan-300 hover:text-white hover:bg-cyan-500/20"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="h-5 w-5" />
            </Button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1.5 overflow-y-auto">
            <div className="text-[10px] text-cyan-400/50 uppercase tracking-widest font-medium px-3 py-2">
              Navigation
            </div>
            {navigation.map((item, index) => {
              // Check if item is active - exact match for "/" or prefix match for others
              const isActive = item.exact
                ? location.pathname === item.href
                : location.pathname === item.href || location.pathname.startsWith(item.href + "/");
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`
                    group relative flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium
                    transition-all duration-200 overflow-hidden
                    ${isActive
                      ? "bg-cyan-500/15 text-cyan-300 border border-cyan-500/30"
                      : "text-cyan-100/60 hover:text-cyan-100 hover:bg-white/5 border border-transparent"
                    }
                  `}
                  onClick={() => setSidebarOpen(false)}
                  style={{ animationDelay: `${index * 50}ms` }}
                >
                  {/* Active indicator line */}
                  {isActive && (
                    <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 rounded-r bg-cyan-400 shadow-lg shadow-cyan-400/50" />
                  )}

                  {/* Icon container */}
                  <div className={`
                    p-1.5 rounded-md transition-colors
                    ${isActive
                      ? "bg-cyan-500/20"
                      : "bg-white/5 group-hover:bg-cyan-500/10"
                    }
                  `}>
                    <item.icon className={`h-4 w-4 ${isActive ? "text-cyan-400" : ""}`} />
                  </div>

                  <span className="flex-1">{item.name}</span>

                  {/* Hover arrow */}
                  <ChevronRight className={`
                    h-4 w-4 opacity-0 -translate-x-2 transition-all duration-200
                    ${isActive ? "opacity-100 translate-x-0" : "group-hover:opacity-50 group-hover:translate-x-0"}
                  `} />
                </Link>
              );
            })}

            {/* Tools Section - hidden when empty */}
            {tools.length > 0 && (
              <>
                <div className="text-[10px] text-amber-400/50 uppercase tracking-widest font-medium px-3 py-2 mt-4">
                  Tools
                </div>
                {tools.map((item, index) => {
                  const isActive = location.pathname === item.href;
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={`
                        group relative flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium
                        transition-all duration-200 overflow-hidden
                        ${isActive
                          ? "bg-amber-500/15 text-amber-300 border border-amber-500/30"
                          : "text-cyan-100/60 hover:text-amber-100 hover:bg-white/5 border border-transparent"
                        }
                      `}
                      onClick={() => setSidebarOpen(false)}
                      style={{ animationDelay: `${(navigation.length + index) * 50}ms` }}
                    >
                      {/* Active indicator line */}
                      {isActive && (
                        <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 rounded-r bg-amber-400 shadow-lg shadow-amber-400/50" />
                      )}

                      {/* Icon container */}
                      <div className={`
                        p-1.5 rounded-md transition-colors
                        ${isActive
                          ? "bg-amber-500/20"
                          : "bg-white/5 group-hover:bg-amber-500/10"
                        }
                      `}>
                        <item.icon className={`h-4 w-4 ${isActive ? "text-amber-400" : ""}`} />
                      </div>

                      <span className="flex-1">{item.name}</span>

                      {/* Hover arrow */}
                      <ChevronRight className={`
                        h-4 w-4 opacity-0 -translate-x-2 transition-all duration-200
                        ${isActive ? "opacity-100 translate-x-0" : "group-hover:opacity-50 group-hover:translate-x-0"}
                      `} />
                    </Link>
                  );
                })}
              </>
            )}
          </nav>

          {/* User section */}
          <div className="p-4 border-t border-border/30">
            <div className="flex items-center gap-3 px-3 py-2 rounded-lg bg-white/5">
              <Avatar className="h-9 w-9 ring-2 ring-cyan-500/30">
                <AvatarFallback className="bg-cyan-500/20 text-cyan-300 text-sm font-medium">
                  {user?.email?.charAt(0).toUpperCase() || "U"}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">{user?.name || "User"}</p>
                <p className="text-[11px] text-cyan-300/50 truncate">{user?.email}</p>
              </div>
            </div>
          </div>

          {/* Footer spacer */}
          <div className="px-5 py-3 border-t border-border/30" />
        </div>

      </aside>

      {/* Main content */}
      <div className="lg:pl-72 min-h-screen flex flex-col">
        {/* Top bar */}
        <header className="sticky top-0 z-30 h-16 border-b bg-background/80 backdrop-blur-xl supports-[backdrop-filter]:bg-background/60">
          <div className="flex h-full items-center justify-between px-4 lg:px-6">
            {/* Mobile menu button */}
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={() => setSidebarOpen(true)}
            >
              <Menu className="h-5 w-5" />
            </Button>

            {/* Spacer */}
            <div className="hidden lg:flex items-center gap-3" />

            <div className="flex-1" />

            {/* Right side actions */}
            <div className="flex items-center gap-2">
              {/* Theme switcher */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-9 w-9 rounded-lg hover:bg-cyan-500/10"
                  >
                    {resolvedTheme === "dark" ? (
                      <Moon className="h-4 w-4 text-cyan-400" />
                    ) : (
                      <Sun className="h-4 w-4 text-amber-500" />
                    )}
                    <span className="sr-only">Toggle theme</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-40">
                  <DropdownMenuItem
                    onClick={() => setTheme("light")}
                    className={theme === "light" ? "bg-accent" : ""}
                  >
                    <Sun className="mr-2 h-4 w-4" />
                    Light
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => setTheme("dark")}
                    className={theme === "dark" ? "bg-accent" : ""}
                  >
                    <Moon className="mr-2 h-4 w-4" />
                    Dark
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => setTheme("system")}
                    className={theme === "system" ? "bg-accent" : ""}
                  >
                    <Monitor className="mr-2 h-4 w-4" />
                    System
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* User menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    className="relative h-9 w-9 rounded-full ring-2 ring-transparent hover:ring-cyan-500/30 transition-all"
                  >
                    <Avatar className="h-9 w-9">
                      <AvatarFallback className="bg-cyan-500/20 text-cyan-600 dark:text-cyan-400">
                        {user?.email?.charAt(0).toUpperCase() || "U"}
                      </AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>
                    <div className="flex flex-col space-y-1">
                      <p className="text-sm font-medium">{user?.name || "User"}</p>
                      <p className="text-xs text-muted-foreground">{user?.email}</p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/settings" className="cursor-pointer">
                      <Settings className="mr-2 h-4 w-4" />
                      {t("nav.settings")}
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-red-500 focus:text-red-500">
                    <LogOut className="mr-2 h-4 w-4" />
                    {t("nav.logout")}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 p-6 lg:p-8">
          <Outlet />
        </main>

        {/* Footer */}
        <footer className="border-t border-border bg-card/30 py-5 px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-muted-foreground">
            <div className="flex items-center gap-3">
              <div className="h-6 w-6 rounded-lg bg-gradient-to-br from-cyan-500/20 to-cyan-600/20 flex items-center justify-center border border-cyan-500/20">
                <Radar className="h-3 w-3 text-cyan-500" />
              </div>
              <span>
                © {new Date().getFullYear()} Sertua. {t("footer.rights", "All rights reserved.")}
              </span>
            </div>
            <span className="font-mono text-muted-foreground/60">v1.0.0</span>
          </div>
        </footer>
      </div>
    </div>
  );
}
